import * as React from 'react';
import { format } from 'date-fns';
import { CalendarRange as CalendarIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Calendar } from '@epoch-ui/components/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@epoch-ui/components/Popover/popover';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription
} from '@epoch-ui/components/Form/form';

export interface DatePickerFieldProps {
  name: string;
  label?: string | React.ReactNode | React.ReactNode[];
  className?: string;
  inputClassName?: string;
  description?: string | React.ReactNode | React.ReactNode[];
  disabled?: (date: Date) => boolean;
  isDisabled?: boolean;
  placeholder?: string;
  popoverAlign?: 'start' | 'center' | 'end';
}

export const DatePickerField = React.forwardRef<HTMLButtonElement, DatePickerFieldProps>(
  (
    {
      name,
      label,
      description,
      className,
      disabled: customDisabledDate,
      isDisabled,
      popoverAlign = 'start'
    },
    ref
  ): React.ReactElement => {
    const { control } = useFormContext();
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    const tomorrow = React.useMemo(() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(0, 0, 0, 0);
      return d;
    }, []);

    const today = React.useMemo(() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    }, []);

    const defaultDisabledDate = (date: Date): boolean => date < today;

    const handleDateSelect = (
      selectedDate: Date | undefined,
      fieldOnChange: (value: Date | undefined) => void
    ): void => {
      if (selectedDate) {
        // Only update form value if a valid date is selected
        fieldOnChange(selectedDate);
      }
      // Always close the popover after an interaction
      setIsPopoverOpen(false);
    };

    return (
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }): React.ReactElement => (
          <FormItem className={cn('', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            {description && <FormDescription>{description}</FormDescription>}
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    ref={ref}
                    id={name}
                    variant={'outline'}
                    className={cn(
                      'tw-flex tw-w-full tw-items-center tw-justify-between tw-text-left tw-text-sm tw-font-normal',
                      'tw-h-[48px] tw-w-[150px]',
                      fieldState.error &&
                        'tw-border-destructive-foreground tw-ring-destructive-foreground focus:tw-border-destructive-foreground'
                    )}
                    type="button"
                    onClick={(): void => setIsPopoverOpen((prev) => !prev)}
                    disabled={isDisabled}
                  >
                    <span>
                      {field.value
                        ? format(field.value as Date, 'M/d/yyyy')
                        : format(tomorrow, 'M/d/yyyy')}
                    </span>
                    <CalendarIcon className="tw-ml-2 tw-h-6 tw-w-6 tw-text-medium-grey" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="tw-w-auto tw-p-0" align={popoverAlign}>
                <Calendar
                  mode="single"
                  selected={(field.value || tomorrow) as Date | undefined} // Reflect default in popover
                  defaultMonth={field.value || tomorrow} // Open popover to the month of selected/default date
                  onSelect={(date): void => handleDateSelect(date, field.onChange)}
                  disabled={customDisabledDate || defaultDisabledDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </FormItem>
        )}
      />
    );
  }
);

DatePickerField.displayName = 'DatePickerField';
